import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  SheetClose
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ColorEditorPopover } from '@/components/feature-configs/autoplay/ColorEditorPopover';
import { HeadlineOverlay, HeadlineOverlayVariation } from '@/store/videoFeatures/types/headline-overlay';
import { toast } from 'sonner';
import { X, Type, MessageSquare } from 'lucide-react';

interface HeadlineVariationSheetProps {
  open: boolean;
  onClose: () => void;
  onSave: (variation: Omit<HeadlineOverlayVariation, 'id' | 'metrics'>) => void;
  existingVariation?: HeadlineOverlayVariation;
  currentConfig: HeadlineOverlay;
}

export const HeadlineVariationSheet: React.FC<HeadlineVariationSheetProps> = ({
  open,
  onClose,
  onSave,
  existingVariation,
  currentConfig
}) => {
  useLanguage();
  const MIN_PX = 10;
  const MAX_PX = 80;

  const resolveInitialSize = (raw: any, defaultPx: number): number => {
    if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) return raw;
    if (typeof raw === 'string') {
      const num = parseInt(raw, 10);
      if (!isNaN(num) && num > 0) return num;
    }
    return defaultPx;
  };

  const initialHeadlineSize = resolveInitialSize(
    existingVariation?.headline?.size ?? currentConfig.headline.size,
    24,
  );
  const initialSubheadlineSize = resolveInitialSize(
    existingVariation?.subheadline?.size ?? currentConfig.subheadline.size,
    16,
  );

  const [formData, setFormData] = useState({
    name: existingVariation?.name || '',
    headline: {
      text: existingVariation?.headline?.text || currentConfig.headline.text || '',
      color: existingVariation?.headline?.color || currentConfig.headline.color,
      size: initialHeadlineSize,
      fontFamily: existingVariation?.headline?.fontFamily || currentConfig.headline.fontFamily,
      fontWeight: existingVariation?.headline?.fontWeight || currentConfig.headline.fontWeight
    },
    subheadline: {
      text: existingVariation?.subheadline?.text || currentConfig.subheadline.text || '',
      color: existingVariation?.subheadline?.color || currentConfig.subheadline.color,
      size: initialSubheadlineSize,
      fontFamily: existingVariation?.subheadline?.fontFamily || currentConfig.subheadline.fontFamily,
      fontWeight: existingVariation?.subheadline?.fontWeight || currentConfig.subheadline.fontWeight
    }
  });

  // Estado local (string) dos inputs de tamanho — permite o campo ficar
  // vazio durante a digitação sem o fallback empurrar 24/16 de volta.
  const [localHeadlineSize, setLocalHeadlineSize] = useState<string>(String(initialHeadlineSize));
  const [localSubheadlineSize, setLocalSubheadlineSize] = useState<string>(String(initialSubheadlineSize));

  useEffect(() => {
    if (open) {
      const h = resolveInitialSize(existingVariation?.headline?.size ?? currentConfig.headline.size, 24);
      const s = resolveInitialSize(existingVariation?.subheadline?.size ?? currentConfig.subheadline.size, 16);
      setFormData({
        name: existingVariation?.name || '',
        headline: {
          text: existingVariation?.headline?.text || currentConfig.headline.text || '',
          color: existingVariation?.headline?.color || currentConfig.headline.color,
          size: h,
          fontFamily: existingVariation?.headline?.fontFamily || currentConfig.headline.fontFamily,
          fontWeight: existingVariation?.headline?.fontWeight || currentConfig.headline.fontWeight
        },
        subheadline: {
          text: existingVariation?.subheadline?.text || currentConfig.subheadline.text || '',
          color: existingVariation?.subheadline?.color || currentConfig.subheadline.color,
          size: s,
          fontFamily: existingVariation?.subheadline?.fontFamily || currentConfig.subheadline.fontFamily,
          fontWeight: existingVariation?.subheadline?.fontWeight || currentConfig.subheadline.fontWeight
        }
      });
      setLocalHeadlineSize(String(h));
      setLocalSubheadlineSize(String(s));
    }
  }, [open, existingVariation, currentConfig]);

  // Clamp + normalização no blur. Durante o typing o input pode ficar vazio.
  const commitSizeOnBlur = (
    raw: string,
    defaultPx: number,
    apply: (v: number) => void,
    setLocal: (v: string) => void,
  ) => {
    const parsed = parseInt(raw, 10);
    let next = defaultPx;
    if (Number.isFinite(parsed)) {
      next = Math.min(MAX_PX, Math.max(MIN_PX, parsed));
    }
    setLocal(String(next));
    apply(next);
  };

  const handleSave = () => {
    if (!formData.headline.text.trim() && !formData.subheadline.text.trim()) {
      toast.error(translateInterfaceText("Preencha Headline ou Subheadline."));
      return;
    }

    if (!formData.name.trim()) {
      toast.error(translateInterfaceText("Digite um nome para a variação"));
      return;
    }

    onSave(formData);
    toast.success(translateInterfaceText("Variação salva!"));
    onClose(); // SEMPRE fecha
  };

  const updateHeadline = (key: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      headline: { ...prev.headline, [key]: value }
    }));
  };

  const updateSubheadline = (key: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      subheadline: { ...prev.subheadline, [key]: value }
    }));
  };

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent 
        side="right" 
        className="w-[400px] sm:max-w-[420px] p-0 flex flex-col overflow-hidden"
        hideCloseButton
      >
        <SheetHeader className="px-6 py-4 border-b sticky top-0 bg-background z-10">
          <div className="flex items-center justify-between">
            <SheetTitle>
              {existingVariation ? translateInterfaceText("Editar Variação de Headline") : translateInterfaceText("Nova Variação de Headline")}
            </SheetTitle>
            <SheetClose asChild>
              <Button variant="ghost" size="icon" className="h-6 w-6">
                <X className="h-4 w-4" />
              </Button>
            </SheetClose>
          </div>
        </SheetHeader>

        <div className="flex-1 px-6 py-6 space-y-8 overflow-y-auto premium-scroll pr-4 pb-10">
          {/* 🔵 Bloco 1 - Nome da Variação */}
          <div className="space-y-3">
            <Label className="text-sm font-semibold">{translateInterfaceText("Nome da Variação")}</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder={translateInterfaceText("Ex: Headline Moderna, Conversão Limpa...")}
            />
            <p className="text-xs text-muted-foreground">
              {translateInterfaceText("Nome usado apenas para organização interna.")}</p>
          </div>

          {/* 🔵 Bloco 2 - Headline Principal */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Type className="h-4 w-4 text-primary" />
              <Label className="text-sm font-semibold">{translateInterfaceText("Headline Principal")}</Label>
            </div>
            
            {/* Texto da Headline */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">{translateInterfaceText("Texto da Headline")}</Label>
              <Input
                value={formData.headline.text}
                onChange={(e) => updateHeadline('text', e.target.value)}
                placeholder={translateInterfaceText("Digite o texto principal...")}
              />
            </div>
            
            {/* Grid 2 colunas - Tamanho + Peso */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">{translateInterfaceText("Tamanho da Fonte (px)")}</Label>
                <Input
                  type="number"
                  min={MIN_PX}
                  max={MAX_PX}
                  step={1}
                  value={localHeadlineSize}
                  onChange={(e) => {
                    const str = e.target.value;
                    setLocalHeadlineSize(str);
                    const v = parseInt(str, 10);
                    if (Number.isFinite(v) && v >= MIN_PX && v <= MAX_PX) {
                      updateHeadline('size', v);
                    }
                  }}
                  onBlur={() =>
                    commitSizeOnBlur(localHeadlineSize, 24, (v) => updateHeadline('size', v), setLocalHeadlineSize)
                  }
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">{translateInterfaceText("Peso da Fonte")}</Label>
                <Select value={formData.headline.fontWeight} onValueChange={(value) => updateHeadline('fontWeight', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">{translateInterfaceText("Normal")}</SelectItem>
                    <SelectItem value="semibold">{translateInterfaceText("Semi Bold")}</SelectItem>
                    <SelectItem value="bold">{translateInterfaceText("Bold")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Grid - Família + Cor */}
            <div className="flex items-end gap-4">
              <div className="flex-1 space-y-2">
                <Label className="text-xs text-muted-foreground">{translateInterfaceText("Família da Fonte")}</Label>
                <Select value={formData.headline.fontFamily} onValueChange={(value) => updateHeadline('fontFamily', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Inter">{translateInterfaceText("Inter")}</SelectItem>
                    <SelectItem value="Poppins">{translateInterfaceText("Poppins")}</SelectItem>
                    <SelectItem value="Roboto">{translateInterfaceText("Roboto")}</SelectItem>
                    <SelectItem value="Oswald">{translateInterfaceText("Oswald")}</SelectItem>
                    <SelectItem value="Montserrat">{translateInterfaceText("Montserrat")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex flex-col items-center gap-1.5">
                <span className="text-[11px] text-muted-foreground font-medium text-center">{translateInterfaceText("Cor")}</span>
                <ColorEditorPopover
                  colorName={translateInterfaceText("Cor da Headline")}
                  currentColor={formData.headline.color}
                  onSave={(color) => updateHeadline('color', color)}
                >
                  <button
                    type="button"
                    className="w-10 h-10 rounded-full border-2 border-white shadow-md hover:scale-110 transition-transform cursor-pointer"
                    style={{ backgroundColor: formData.headline.color }}
                  />
                </ColorEditorPopover>
              </div>
            </div>
          </div>

          {/* 🔵 Bloco 3 - Subheadline (Opcional) */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-primary" />
              <Label className="text-sm font-semibold">{translateInterfaceText("Subheadline (Opcional)")}</Label>
            </div>
            
            {/* Texto da Subheadline */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">{translateInterfaceText("Texto da Subheadline")}</Label>
              <Input
                value={formData.subheadline.text}
                onChange={(e) => updateSubheadline('text', e.target.value)}
                placeholder={translateInterfaceText("Digite o texto secundário...")}
              />
            </div>
            
            {/* Grid 2 colunas - Tamanho + Peso */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">{translateInterfaceText("Tamanho da Fonte (px)")}</Label>
                <Input
                  type="number"
                  min={MIN_PX}
                  max={MAX_PX}
                  step={1}
                  value={localSubheadlineSize}
                  onChange={(e) => {
                    const str = e.target.value;
                    setLocalSubheadlineSize(str);
                    const v = parseInt(str, 10);
                    if (Number.isFinite(v) && v >= MIN_PX && v <= MAX_PX) {
                      updateSubheadline('size', v);
                    }
                  }}
                  onBlur={() =>
                    commitSizeOnBlur(localSubheadlineSize, 16, (v) => updateSubheadline('size', v), setLocalSubheadlineSize)
                  }
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">{translateInterfaceText("Peso da Fonte")}</Label>
                <Select value={formData.subheadline.fontWeight} onValueChange={(value) => updateSubheadline('fontWeight', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">{translateInterfaceText("Normal")}</SelectItem>
                    <SelectItem value="semibold">{translateInterfaceText("Semi Bold")}</SelectItem>
                    <SelectItem value="bold">{translateInterfaceText("Bold")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Grid - Família + Cor */}
            <div className="flex items-end gap-4">
              <div className="flex-1 space-y-2">
                <Label className="text-xs text-muted-foreground">{translateInterfaceText("Família da Fonte")}</Label>
                <Select value={formData.subheadline.fontFamily} onValueChange={(value) => updateSubheadline('fontFamily', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Inter">{translateInterfaceText("Inter")}</SelectItem>
                    <SelectItem value="Poppins">{translateInterfaceText("Poppins")}</SelectItem>
                    <SelectItem value="Roboto">{translateInterfaceText("Roboto")}</SelectItem>
                    <SelectItem value="Oswald">{translateInterfaceText("Oswald")}</SelectItem>
                    <SelectItem value="Montserrat">{translateInterfaceText("Montserrat")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex flex-col items-center gap-1.5">
                <span className="text-[11px] text-muted-foreground font-medium text-center">{translateInterfaceText("Cor")}</span>
                <ColorEditorPopover
                  colorName={translateInterfaceText("Cor da Subheadline")}
                  currentColor={formData.subheadline.color}
                  onSave={(color) => updateSubheadline('color', color)}
                >
                  <button
                    type="button"
                    className="w-10 h-10 rounded-full border-2 border-white shadow-md hover:scale-110 transition-transform cursor-pointer"
                    style={{ backgroundColor: formData.subheadline.color }}
                  />
                </ColorEditorPopover>
              </div>
            </div>
          </div>
        </div>

        <SheetFooter className="px-6 py-3 border-t sticky bottom-0 bg-background z-10">
          <Button 
            onClick={handleSave} 
            className="w-full"
          >
            {translateInterfaceText("Salvar alterações")}</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
